<script lang="ts" setup>
import { ref, computed, Ref, inject, provide, onBeforeMount, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import { useAlerts } from '@/plugins/alerts';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { DocumentEntry, DocumentType, instanceOfDocumentEntry } from '.';
import { FormEntry } from '../form';
import StorageService from '@/modules/core/files/services/StorageService';
import properties from '../../properties';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'document-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

provide('documentName', props.blueprint.name ?? '');

const { $events } = useEvents();
const { $alert } = useAlerts();
const level = inject<number>('level', 0);

const entryData = ref(new DocumentEntry()) as Ref<DocumentEntry>;

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfDocumentEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);

        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();

        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        return value != null ? [value] : null;
    },
    set(value: any[])
    {
        if (value == null)
        {
            entryData.value.data = null;
            uploadFiles(null);

            return;
        }

        if (Array.isArray(value)) value = value[0];

        uploadFiles(value);
        entryData.value.data = value;
        useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    },
});

const blueprint = computed(() => props.blueprint);
const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const extensions = computed(() =>
    props.blueprint.extensions
        .split(',')
        .map((p) => p.trim())
        .filter((p) => !!p)
);

const uploadFiles = async (file: any) =>
{
    try
    {
        if (file && file.upload)
        {
            const uploaded = await StorageService.upload(file.upload, 'documents');

            uploaded.upload = null;

            entryData.value.data = uploaded;
            $events.$emit(`file-to-preview-${level}`, uploaded, blueprint.value.name);
        }
        else
        {
            clearFiles();
        }
    }
    catch (ex: any)
    {
        $alert.danger(ex.message);
        clearFiles();
    }
};

const clearFiles = () =>
{
    entryData.value.data = null;
    $events.$emit(`file-to-preview-${level}`, null, blueprint.value.name);
};

const init = () =>
{
    $events.$emit(`file-to-preview-${level}`, entryData.value.data ?? null, blueprint.value.name);
};

init();
</script>

<template>
    <form-component-wrapper class="document-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <fileupload
                    v-model="value"
                    :id="blueprint.id"
                    :max-size="blueprint.maxFileSize"
                    :max-files="blueprint.maxFiles"
                    :extensions="extensions"
                    :dark="false"
                    :margin-bottom="false"
                    :readonly="readonly"
                />
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-text v-model="blueprint.extensions" :label="$t('[[[Dozwolone rozszerzenia]]]')" />
                    <field-numeric
                        v-model="blueprint.maxFileSize"
                        :label="$t('[[[Maksymalny rozmiar pliku w MB]]]')"
                        :min="0"
                        :max="100"
                    />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
                    <field-error :form="form" :blueprint="blueprint" :label="$t('[[[Niestandardowy błąd]]]')" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
